// Debug logger utility that saves logs to localStorage for persistence across page refreshes
// Only shows logs for dev accounts

import { DEV_UIDS } from '../config/tiers';

const MAX_LOGS = 100;
const LOGS_KEY = 'debug_logs';
const DEBUG_MODE_KEY = 'debug_mode_enabled';
const DEV_UID_KEY = 'dev_uid_cached';

/**
 * Check if the current user is a dev (by cached UID)
 * Call setDevUser() when user logs in to cache their status
 */
export const isDevUser = () => {
  if (typeof window === 'undefined') return false;
  
  // Check if debug mode is manually enabled
  if (window.debugMode || localStorage.getItem(DEBUG_MODE_KEY) === 'true') {
    return true;
  }
  
  // Check cached dev UID
  const cachedUid = localStorage.getItem(DEV_UID_KEY);
  return cachedUid && DEV_UIDS.includes(cachedUid);
};

/**
 * Cache the user's UID for dev checking (call on auth state change)
 */
export const setDevUser = (uid) => {
  if (typeof window === 'undefined') return;
  
  if (uid && DEV_UIDS.includes(uid)) {
    localStorage.setItem(DEV_UID_KEY, uid);
    console.log('[Debug] Dev user detected, logging enabled');
  } else {
    localStorage.removeItem(DEV_UID_KEY);
  }
};

/**
 * Clear dev user cache (call on logout)
 */
export const clearDevUser = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(DEV_UID_KEY);
  }
};

/**
 * Dev-only console.log - only outputs if user is a dev
 */
export const devLog = (...args) => {
  if (isDevUser()) {
    console.log(...args);
  }
};

/**
 * Dev-only console.warn - only outputs if user is a dev
 */
export const devWarn = (...args) => {
  if (isDevUser()) {
    console.warn(...args);
  }
};

/**
 * Dev-only console.error - always shows (errors are important)
 */
export const devError = (...args) => {
  console.error(...args);
};

export const initDebugLogger = () => {
  // Restore debug mode from localStorage (persists across page refreshes)
  if (typeof window !== 'undefined') {
    const savedDebugMode = localStorage.getItem(DEBUG_MODE_KEY);
    if (savedDebugMode === 'true') {
      window.debugMode = true;
    }
  }
  
  // Override console.log when debugMode is enabled
  const originalLog = console.log;
  const originalError = console.error;
  const originalWarn = console.warn;

  const saveLog = (level, args) => {
    try {
      if (typeof window !== 'undefined' && window.debugMode) {
        const message = args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
        ).join(' ');
        
        const timestamp = new Date().toLocaleTimeString();
        const logEntry = `[${timestamp}] [${level}] ${message}`;
        
        // Save to localStorage
        let logs = [];
        try {
          logs = JSON.parse(localStorage.getItem(LOGS_KEY) || '[]');
        } catch (e) {
          logs = [];
        }
        
        logs.push(logEntry);
        if (logs.length > MAX_LOGS) {
          logs = logs.slice(-MAX_LOGS); // Keep only last 100 logs
        }
        
        localStorage.setItem(LOGS_KEY, JSON.stringify(logs));
      }
    } catch (e) {
      // Silently fail if localStorage is unavailable
    }
  };

  console.log = function(...args) {
    originalLog.apply(console, args);
    saveLog('LOG', args);
  };

  console.error = function(...args) {
    originalError.apply(console, args);
    saveLog('ERROR', args);
  };

  console.warn = function(...args) {
    originalWarn.apply(console, args);
    saveLog('WARN', args);
  };

  // Expose utility function to view/clear logs
  window.getDebugLogs = () => {
    try {
      const logs = JSON.parse(localStorage.getItem(LOGS_KEY) || '[]');
      console.log('=== DEBUG LOGS ===');
      logs.forEach(log => console.log(log));
      console.log('=== END DEBUG LOGS ===');
      return logs;
    } catch (e) {
      console.error('Failed to retrieve logs:', e);
      return [];
    }
  };

  window.clearDebugLogs = () => {
    localStorage.removeItem(LOGS_KEY);
    console.log('Debug logs cleared');
  };

  // Enable debug mode persistently (survives page refresh)
  window.enableDebug = () => {
    window.debugMode = true;
    localStorage.setItem(DEBUG_MODE_KEY, 'true');
    console.log('Debug mode enabled (persists across page refresh)');
  };

  // Disable debug mode
  window.disableDebug = () => {
    window.debugMode = false;
    localStorage.removeItem(DEBUG_MODE_KEY);
    console.log('Debug mode disabled');
  };

  window.downloadDebugLogs = () => {
    try {
      const logs = JSON.parse(localStorage.getItem(LOGS_KEY) || '[]');
      const text = logs.join('\n');
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `debug-logs-${new Date().toISOString()}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to download logs:', e);
    }
  };

  // Help command to show available debug commands
  window.debugHelp = () => {
    console.log('%c🔧 Debug Commands:', 'font-weight: bold; font-size: 14px;');
    console.log('  enableDebug()       - Enable console logging (persists across refresh)');
    console.log('  disableDebug()      - Disable console logging');
    console.log('  getDebugLogs()      - View saved debug logs');
    console.log('  clearDebugLogs()    - Clear saved logs');
    console.log('  downloadDebugLogs() - Download logs as a text file');
    console.log('  debugHelp()         - Show this help message');
  };
};
